import { useEffect, useState } from 'react'
import type { EditorialAuthor, EditorialReviewStatus } from '../data/editorialPersonas'

export interface StateOfAiSource { id: string; title: string; url: string; publisher: string; publishedAt: string; evidenceType?: 'full-text' | 'partial-text' | 'feed-summary' }

export interface StateOfAiSummary {
  id: string
  date: string
  generatedAt: string
  publishedAt?: string
  revisedAt?: string
  evidenceUpdatedThrough?: string
  headline: string
  dek: string
  sourceCount: number
  fullTextSourceCount?: number
  partialTextSourceCount?: number
  feedSummarySourceCount?: number
  evidenceMode?: string
  imageUrl?: string
  imageAlt?: string
  imageModel?: string
  imageGeneratedAt?: string
  author?: EditorialAuthor
  editorialReview?: EditorialReviewStatus
  humanReview?: { reviewerName: string; reviewerRole?: string; reviewedAt: string }
  homepagePromotionEligible?: boolean
  homepagePromotionStatus?: 'eligible' | 'awaiting-human-review'
}

export interface StateOfAiEdition extends StateOfAiSummary {
  executiveSummary: string
  model: string
  mode: 'direct' | 'hierarchical'
  developments: Array<{ heading: string; analysis: string; whyItMatters: string; sourceIds: string[] }>
  watchNext: Array<{ text: string; sourceIds: string[] }>
  sources: StateOfAiSource[]
}

export interface StateOfAiQuery {
  id?: string
  limit?: number
  summary?: boolean
}

interface StateOfAiState<T> {
  editions: T[]
  loading: boolean
  error: string | null
}

const LATEST_POLL_INTERVAL_MS = 60 * 60 * 1000

export function stateOfAiEndpoint({ id, limit, summary }: StateOfAiQuery = {}): string {
  const params = new URLSearchParams()
  if (id) params.set('id', id)
  if (limit !== undefined) params.set('limit', String(Math.min(60, Math.max(1, Math.floor(limit)))))
  if (summary) params.set('summary', 'true')
  const query = params.toString()
  return `/api/state-of-ai${query ? `?${query}` : ''}`
}

function useStateOfAiRequest<T>(enabled: boolean, query: StateOfAiQuery, pollIntervalMs = 0): StateOfAiState<T> {
  const [editions, setEditions] = useState<T[]>([])
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const { id, limit, summary } = query

  useEffect(() => {
    if (!enabled) return
    let disposed = false
    let controller: AbortController | null = null
    let lastRequestAt = 0

    const load = async (showLoading = false) => {
      controller?.abort()
      controller = new AbortController()
      lastRequestAt = Date.now()
      if (showLoading) setLoading(true)
      try {
        const response = await fetch(stateOfAiEndpoint({ id, limit, summary }), {
          signal: controller.signal,
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
        if (!response.ok) throw new Error(String(response.status))
        const payload = await response.json() as { editions?: T[] }
        if (!disposed) {
          setEditions(Array.isArray(payload.editions) ? payload.editions : [])
          setError(null)
        }
      } catch (cause) {
        if (!disposed && (cause as Error).name !== 'AbortError') setError('STATE OF AI TEMPORARILY UNAVAILABLE')
      } finally {
        if (!disposed) setLoading(false)
      }
    }

    void load(true)
    const interval = pollIntervalMs > 0 ? window.setInterval(() => void load(), pollIntervalMs) : undefined
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible' && pollIntervalMs > 0 && Date.now() - lastRequestAt >= pollIntervalMs) void load()
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      disposed = true
      controller?.abort()
      if (interval !== undefined) window.clearInterval(interval)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [enabled, id, limit, pollIntervalMs, summary])

  return { editions, loading: enabled && loading, error }
}

export function useStateOfAi(enabled = true, id?: string): StateOfAiState<StateOfAiEdition> {
  return useStateOfAiRequest<StateOfAiEdition>(enabled, { id })
}

export function useLatestStateOfAi(enabled = true): StateOfAiState<StateOfAiSummary> {
  return useStateOfAiRequest<StateOfAiSummary>(enabled, { limit: 1, summary: true }, LATEST_POLL_INTERVAL_MS)
}
